import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Badge, Button, Dropdown, List, Typography, Spin } from 'antd';
import { BellOutlined, CheckOutlined } from '@ant-design/icons';
import { useAppTheme } from '../../theme/ThemeProvider';
import { useTranslation } from 'react-i18next';
import { AlertItem, fetchAlerts, fetchUnreadCount, markAlertRead, markAllAlertsRead } from '../../services/alertService';

const { Text } = Typography;

function formatUtc(ms: number): string {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleString('en-US', { timeZone: 'UTC', month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' UTC';
  } catch {
    return '';
  }
}

export const NotificationBell: React.FC = () => {
  const { theme } = useAppTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const lastRefreshAtRef = useRef<number>(0);

  const refreshUnread = async () => {
    try {
      const cnt = await fetchUnreadCount();
      setUnread(cnt);
    } catch {
      // ignore
    }
  };

  const refreshAlerts = async () => {
    setLoading(true);
    try {
      const list = await fetchAlerts(50);
      setAlerts(list);
      const cnt = list.filter(a => !a.is_read).length;
      setUnread(cnt);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial load
    refreshUnread();

    // Refresh when backend signals "data_updated" (candle close) — alerts are created on candle close too.
    const onDataUpdated = () => {
      const now = Date.now();
      // throttle to avoid spamming (multiple streams may close around the same time)
      if (now - lastRefreshAtRef.current < 1500) return;
      lastRefreshAtRef.current = now;
      refreshUnread();
    };

    // Refresh when tab becomes active again
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onDataUpdated();
    };

    window.addEventListener('backend_data_updated', onDataUpdated as any);
    window.addEventListener('focus', onDataUpdated);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('backend_data_updated', onDataUpdated as any);
      window.removeEventListener('focus', onDataUpdated);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    if (open) refreshAlerts();
  }, [open]);

  const items = useMemo(() => {
    if (loading) {
      return (
        <div style={{ padding: 12, width: 360 }}>
          <Spin size="small" />
        </div>
      );
    }
    return (
      <div style={{ width: 360 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: `1px solid ${theme.surfaceBorder}` }}>
          <Text strong style={{ color: theme.textPrimary, fontSize: 12 }}>
            {t('notification.title', 'Notifications')}
          </Text>
          <Button
            size="small"
            type="text"
            icon={<CheckOutlined />}
            style={{ color: theme.textSecondary, fontSize: 12 }}
            onClick={async () => {
              const updated = await markAllAlertsRead();
              if (updated > 0) {
                setAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
                setUnread(0);
              }
            }}
          >
            {t('notification.markAllRead', 'Mark all read')}
          </Button>
        </div>
        <List
          dataSource={alerts}
          locale={{ emptyText: <Text style={{ color: theme.textSecondary, fontSize: 12 }}>{t('notification.noAlerts', 'No alerts')}</Text> }}
          renderItem={(a) => (
            <List.Item
              style={{
                cursor: 'pointer',
                padding: '10px 12px',
                background: a.is_read ? 'transparent' : theme.surfaceBackground,
                borderBottom: `1px solid ${theme.surfaceBorder}`,
              }}
              onClick={async () => {
                if (!a.is_read) {
                  const ok = await markAlertRead(a.alert_id);
                  if (ok) {
                    setAlerts(prev => prev.map(x => (x.alert_id === a.alert_id ? { ...x, is_read: true } : x)));
                    setUnread(prev => Math.max(0, prev - 1));
                  }
                }
              }}
            >
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <Text strong style={{ color: theme.textPrimary, fontSize: 12 }}>
                    {a.signal_name || a.message || t('notification.alertTriggered', 'Signal triggered')}
                  </Text>
                  {!a.is_read && (
                    <span style={{ width: 8, height: 8, borderRadius: 999, background: theme.candleUp, display: 'inline-block' }} />
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                  <Text style={{ color: theme.textSecondary, fontSize: 11 }}>{a.symbol}@{a.timeframe}</Text>
                  <Text style={{ color: theme.textSecondary, fontSize: 11 }}>{formatUtc(a.trigger_time_ms)}</Text>
                  {typeof a.price === 'number' && (
                    <Text style={{ color: theme.textSecondary, fontSize: 11 }}>{t('notification.price', 'Price')}: {a.price}</Text>
                  )}
                </div>
              </div>
            </List.Item>
          )}
          style={{ maxHeight: 420, overflow: 'auto' }}
        />
      </div>
    );
  }, [alerts, loading, theme, t]);

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      dropdownRender={() => (
        <div style={{ background: theme.surfaceBackground, border: `1px solid ${theme.surfaceBorder}`, borderRadius: 10, overflow: 'hidden' }}>
          {items}
        </div>
      )}
      placement="bottomRight"
      trigger={['click']}
    >
      <Badge count={unread} size="small" overflowCount={99} offset={[-4, 4]}>
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: 18 }} />}
          style={{
            color: theme.textSecondary,
            width: 40,
            height: 40,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px solid ${theme.surfaceBorder}`,
          }}
        />
      </Badge>
    </Dropdown>
  );
};


